import { useRef, useState } from "react";
import { useAgentIconUrl } from "../../hooks/useAgentIconUrl";
import { uploadAgentIconFile } from "../../services/agentIconUpload";
import { PICKABLE_ICONS, iconForRole } from "../../services/agentCatalog";
import type { OceanAgentRole } from "@vida/currents-core";

type Props = {
  orgId: string;
  agentId?: string;
  role: OceanAgentRole;
  emoji: string;
  iconS3Key?: string | null;
  onEmojiChange: (emoji: string) => void;
  onIconS3KeyChange: (key: string | null) => void;
};

export function AgentIconUpload({
  orgId,
  agentId,
  role,
  emoji,
  iconS3Key,
  onEmojiChange,
  onIconS3KeyChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const previewUrl = useAgentIconUrl(iconS3Key);

  async function onFileSelected(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const { s3Key } = await uploadAgentIconFile({ orgId, agentId, file });
      onIconS3KeyChange(s3Key);
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="icon-upload-block">
      <div className="icon-upload-preview">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="agent-avatar agent-avatar-lg" />
        ) : (
          <span className="agent-avatar agent-avatar-lg" aria-hidden>
            {emoji || iconForRole(role)}
          </span>
        )}
      </div>
      <div className="icon-upload-actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={(e) => void onFileSelected(e.target.files?.[0])}
        />
        <button
          type="button"
          className="secondary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? "Caricamento…" : "Carica immagine (S3)"}
        </button>
        {iconS3Key && (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              onIconS3KeyChange(null);
              onEmojiChange(iconForRole(role));
            }}
          >
            Rimuovi immagine
          </button>
        )}
        {uploadErr && <p className="err">{uploadErr}</p>}
        <p className="muted section-hint">Oppure emoji di fallback:</p>
        <div className="icon-picker">
          {PICKABLE_ICONS.map((emo) => (
            <button
              key={emo}
              type="button"
              className={`icon-pick ${emoji === emo && !iconS3Key ? "selected" : ""}`}
              onClick={() => onEmojiChange(emo)}
              aria-label={`Emoji ${emo}`}
            >
              {emo}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
